import { useQuery } from '@tanstack/react-query';
import { ApiError, api } from '../../api';
import { useSession } from '../../session';
import { VEHICLE_AVAILABILITY_LABELS, type DeadlineState, type EmployeeList, type Paginated, type Vehicle } from '@kontor/shared';

export const errorText = (e: unknown, fallback: string) => (e instanceof ApiError ? (e.status === 422 ? (Object.values(e.fields)[0] ?? e.message) : e.message) : fallback);

export const dayFormat = new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', timeZone: 'Europe/Berlin' });
export const dateTimeFormat = new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Berlin' });
export const formatDay = (iso: string | null) => (iso ? dayFormat.format(new Date(iso.length === 10 ? `${iso}T12:00:00` : iso)) : '–');
export const kmText = (km: number) => `${km.toLocaleString('de-DE')} km`;
/** Wandzeit JJJJ-MM-TTTHH:MM als „15.10. 08:00“ */
export const wallText = (wall: string) => `${wall.slice(8, 10)}.${wall.slice(5, 7)}. ${wall.slice(11, 16)}`;
/** Wandzeit mit Jahr: „15.10.2026 08:00“ */
export const wallFull = (wall: string) => `${wall.slice(8, 10)}.${wall.slice(5, 7)}.${wall.slice(0, 4)} ${wall.slice(11, 16)}`;
export const today = () => new Intl.DateTimeFormat('sv-SE', { timeZone: 'Europe/Berlin' }).format(new Date());

/** Mitarbeiter für Fahrer, nur mit Modul und Recht für Mitarbeiter */
export function useEmployees() {
  const { can, hasModule } = useSession();
  return useQuery({
    queryKey: ['employees', 'fleet-options'],
    queryFn: async () => (await api<EmployeeList>('GET', '/employees?status=active')).data,
    enabled: hasModule('hr') && can('hr.read'),
    staleTime: 60_000,
  });
}

/** Fahrzeuge im Einsatz, für Auswahlfelder */
/**
 * Wie oft die Flotte nachlädt. Die Verfügbarkeit (Frei oder Im Einsatz) berechnet der Server aus der Uhrzeit, ändert sich also von selbst, wenn ein Einsatz endet oder beginnt. Damit die
 * Anzeige das ohne Neuladen übernimmt, fragt die Seite regelmäßig nach und nach dem Zurückkehren in den Tab.
 */
export const LIVE_MS = 30_000;

export function useVehicles() {
  return useQuery({
    queryKey: ['fleet-vehicles', 'options'],
    queryFn: async () => (await api<Paginated<Vehicle>>('GET', '/fleet/vehicles?perPage=100&sort=plate')).data,
    staleTime: 30_000,
    refetchInterval: LIVE_MS,
  });
}

/** Schild für die Verfügbarkeit: Frei (grün), Im Einsatz (blau), sonst grau */
export function AvailabilityBadge({ vehicle }: { vehicle: Pick<Vehicle, 'availability'> }) {
  const label = VEHICLE_AVAILABILITY_LABELS[vehicle.availability];
  const cls = vehicle.availability === 'free' ? 'fleet-badge--ok' : vehicle.availability === 'in_use' ? 'fleet-badge--use' : 'fleet-badge--off';
  return <span className={`fleet-badge ${cls}`}>{label}</span>;
}

/** Zusatzzeile zur Verfügbarkeit: bei Einsatz Auftrag, Fahrer und Ende, sonst der nächste Einsatz */
export function availabilityNote(v: Pick<Vehicle, 'availability' | 'inUse' | 'nextAssignmentAt'>): string | null {
  if (v.availability === 'in_use' && v.inUse) {
    if (v.inUse.source === 'driver') return `gefahren von ${v.inUse.employeeName ?? 'einem Fahrer'} (in der App gewählt, online)`;
    const parts = [v.inUse.orderNumber, v.inUse.employeeName, v.inUse.until ? `bis ${wallText(v.inUse.until)}` : v.inUse.source === 'app' ? 'laut App' : null];
    return parts.filter(Boolean).join(' · ') || null;
  }
  if (v.availability === 'free' && v.nextAssignmentAt) return `nächster Einsatz ${wallText(v.nextAssignmentAt)}`;
  return null;
}

/** Text zur nächsten Wartung: „15.03.2027 oder in 1.200 km“ */
export function nextServiceText(s: NonNullable<Vehicle['nextService']>): string {
  const parts: string[] = [];
  if (s.dueOn) parts.push(formatDay(s.dueOn));
  if (s.remainingKm !== null) parts.push(s.remainingKm < 0 ? `${kmText(-s.remainingKm)} überschritten` : `in ${kmText(s.remainingKm)}`);
  return parts.join(' oder ');
}

export const stateLabel: Record<DeadlineState, string> = { overdue: 'überfällig', soon: 'bald fällig', ok: 'in Ordnung' };
export const stateClass: Record<DeadlineState, string> = { overdue: 'fleet-badge--bad', soon: 'fleet-badge--warn', ok: 'fleet-badge--ok' };

export function StateBadge({ state, label }: { state: DeadlineState | null; label?: string }) {
  if (!state) return null;
  return <span className={`fleet-badge ${stateClass[state]}`}>{label ?? stateLabel[state]}</span>;
}
